import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronRight,
  Copy,
  Download,
  History,
  Pencil,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  groesseLesbar,
  ketteZusammensetzen,
  nachkommen,
  pfadZu,
  zeitpunktLesbar,
} from '../../lib/ablage'
import { anhangHochladen, anhangVerweis } from '../../lib/anhang'
import { Markdown } from '../../lib/markdown'
import { dokumentQuery, dokumentVersionenQuery, type DokumentEintrag } from '../../lib/queries'
import { trpc } from '../../lib/trpc'
import { MarkdownFeld } from '../MarkdownFeld'
import { Button } from '../ui/primitives'
import { ArtSymbol } from './AblageBaum'

/**
 * **Ein Dokument der Ablage** – Kopf mit Brotkrumen, Titel und „Fassung n · wer · wann",
 * darunter der Inhalt, der Verlauf und die Handgriffe
 * ([Z-14](../../../../../docs/phasen/zusatz-14-ablage.md)).
 *
 * ## Drei Zustände, eine Komponente
 *
 * | | zeigt |
 * |---|---|
 * | **lesen** | die aktuelle Fassung – Text gerendert, Bild als Bild, Datei als Download |
 * | **bearbeiten** | das Markdown-Feld mit dem Entwurf; Bilder per Ziehen oder Strg+V |
 * | **alte Fassung** | eine frühere Nummer, mit „Wiederherstellen" – nie direkt bearbeitbar |
 *
 * ## Was beim Speichern passieren kann
 *
 * Der Server prüft die Fassung, auf der der Entwurf aufsetzt. Hat inzwischen jemand
 * gespeichert, kommt `CONFLICT` – der Entwurf **bleibt stehen**, der Hinweis nennt den Namen.
 * Nichts wird überschrieben, nichts geht verloren; wer will, kopiert seinen Text und trägt ihn
 * in die neue Fassung ein. Das ist die ehrliche Variante gegenüber einem „letzter gewinnt".
 */
export function DokumentAnsicht({
  dokument,
  alle,
  waehlen,
  nachLoeschen,
}: {
  dokument: DokumentEintrag
  alle: readonly DokumentEintrag[]
  waehlen: (id: string) => void
  nachLoeschen: () => void
}) {
  const queryClient = useQueryClient()
  const [fassungNummer, setFassungNummer] = useState<number | null>(null)
  const [bearbeiten, setBearbeiten] = useState(false)
  const [entwurf, setEntwurf] = useState('')
  const [basis, setBasis] = useState(0)
  const [verlaufOffen, setVerlaufOffen] = useState(false)
  const [verschiebenOffen, setVerschiebenOffen] = useState(false)
  const [titelBearbeiten, setTitelBearbeiten] = useState(false)
  const [titelEntwurf, setTitelEntwurf] = useState(dokument.titel)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [laedtHoch, setLaedtHoch] = useState(false)
  const dateiwahl = useRef<HTMLInputElement>(null)

  /** Ein anderes Dokument – alles zurück auf Anfang, sonst bliebe ein fremder Entwurf stehen. */
  useEffect(() => {
    setFassungNummer(null)
    setBearbeiten(false)
    setVerlaufOffen(false)
    setVerschiebenOffen(false)
    setTitelBearbeiten(false)
    setTitelEntwurf(dokument.titel)
    setFehler(null)
    setHinweis(null)
  }, [dokument.id, dokument.titel])

  const { data: fassung, isLoading } = useQuery({
    ...dokumentQuery(dokument.id, fassungNummer ?? undefined),
    enabled: dokument.art !== 'ordner',
  })
  const { data: versionen = [] } = useQuery({
    ...dokumentVersionenQuery(dokument.id),
    enabled: verlaufOffen,
  })

  const neuLaden = () => queryClient.invalidateQueries({ queryKey: ['dokumente'] })
  const beiFehler = (e: Error) => setFehler(e.message)
  const kurzHinweis = (text: string) => {
    setHinweis(text)
    window.setTimeout(() => setHinweis((h) => (h === text ? null : h)), 2500)
  }

  const speichern = useMutation({
    mutationFn: () =>
      trpc.dokumente.speichern.mutate({ id: dokument.id, inhalt: entwurf, basisNummer: basis }),
    onSuccess: async () => {
      setBearbeiten(false)
      setFehler(null)
      await neuLaden()
    },
    onError: beiFehler,
  })

  const wiederherstellen = useMutation({
    mutationFn: (nummer: number) =>
      trpc.dokumente.wiederherstellen.mutate({ id: dokument.id, nummer }),
    onSuccess: async () => {
      setFassungNummer(null)
      await neuLaden()
      kurzHinweis('Wiederhergestellt – als neue Fassung.')
    },
    onError: beiFehler,
  })

  const umbenennen = useMutation({
    mutationFn: () => trpc.dokumente.umbenennen.mutate({ id: dokument.id, titel: titelEntwurf }),
    onSuccess: async () => {
      setTitelBearbeiten(false)
      await neuLaden()
    },
    onError: beiFehler,
  })

  const verschieben = useMutation({
    mutationFn: (elternId: string | null) =>
      trpc.dokumente.verschieben.mutate({ id: dokument.id, elternId }),
    onSuccess: async () => {
      setVerschiebenOffen(false)
      await neuLaden()
    },
    onError: beiFehler,
  })

  const loeschen = useMutation({
    mutationFn: () => trpc.dokumente.loeschen.mutate({ id: dokument.id }),
    onSuccess: async () => {
      nachLoeschen()
      await neuLaden()
    },
    onError: beiFehler,
  })

  const bearbeitenStarten = () => {
    setEntwurf(fassung?.inhalt ?? '')
    setBasis(fassung?.nummer ?? 0)
    setFassungNummer(null)
    setBearbeiten(true)
    setFehler(null)
  }

  /** Bilder in den Text – hochladen an dieses Dokument, Verweis anhängen (wie in der Beschreibung). */
  async function bilderHochladen(dateien: File[]): Promise<void> {
    setLaedtHoch(true)
    try {
      for (const datei of dateien) {
        const anhang = await anhangHochladen({ dokumentId: dokument.id }, datei)
        setEntwurf((bisher) => `${bisher}${bisher ? '\n\n' : ''}${anhangVerweis(anhang)}`)
      }
    } catch (e) {
      beiFehler(e instanceof Error ? e : new Error('Hochladen fehlgeschlagen'))
    } finally {
      setLaedtHoch(false)
    }
  }

  /** Eine neue Fassung einer Datei – die Route legt Anhang und Fassung in einem Zug an. */
  async function neueDateiFassung(datei: File): Promise<void> {
    setLaedtHoch(true)
    setFehler(null)
    try {
      await anhangHochladen({ dokumentId: dokument.id }, datei)
      setFassungNummer(null)
      await neuLaden()
      kurzHinweis('Neue Fassung hochgeladen.')
    } catch (e) {
      beiFehler(e instanceof Error ? e : new Error('Hochladen fehlgeschlagen'))
    } finally {
      setLaedtHoch(false)
    }
  }

  async function kopieren(mitEltern: boolean): Promise<void> {
    try {
      const text = mitEltern
        ? ketteZusammensetzen(await trpc.dokumente.kette.query({ id: dokument.id }))
        : (fassung?.inhalt ?? '')
      await navigator.clipboard.writeText(text)
      kurzHinweis(mitEltern ? 'Mit Übergeordneten kopiert.' : 'Kopiert.')
    } catch (e) {
      beiFehler(e instanceof Error ? e : new Error('Kopieren fehlgeschlagen'))
    }
  }

  const loeschenFragen = () => {
    const unten = nachkommen(alle, dokument.id).length
    const frage =
      unten === 0
        ? `„${dokument.titel}" mit allen Fassungen löschen?`
        : `„${dokument.titel}" löschen – und die ${unten} ${unten === 1 ? 'Dokument' : 'Dokumente'} darunter gleich mit?`
    if (window.confirm(frage)) loeschen.mutate()
  }

  const pfad = pfadZu(alle, dokument.id)
  const eltern = pfad.slice(0, -1)
  const alteFassung = fassungNummer !== null && fassungNummer !== dokument.nummer
  const ausgeschlossen = new Set([dokument.id, ...nachkommen(alle, dokument.id)])
  const moeglicheEltern = alle.filter((e) => !ausgeschlossen.has(e.id))
  const istBild = fassung?.mimeTyp?.startsWith('image/') ?? false

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {/* ── Brotkrumen ─────────────────────────────────────────────── */}
      {eltern.length > 0 && (
        <nav aria-label="Pfad" className="flex flex-wrap items-center gap-1 text-xs text-muted">
          {eltern.map((e) => (
            <span key={e.id} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => waehlen(e.id)}
                className="cursor-pointer rounded px-1 py-0.5 hover:bg-[#f3f2ef] hover:text-ink"
              >
                {e.titel}
              </button>
              <ChevronRight className="size-3" aria-hidden />
            </span>
          ))}
        </nav>
      )}

      {/* ── Titel ───────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <ArtSymbol eintrag={dokument} className="size-4" />
        {titelBearbeiten ? (
          <form
            className="flex min-w-0 flex-1 items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault()
              if (titelEntwurf.trim()) umbenennen.mutate()
            }}
          >
            <input
              autoFocus
              value={titelEntwurf}
              onChange={(e) => setTitelEntwurf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setTitelBearbeiten(false)
              }}
              aria-label="Titel"
              className="min-w-0 flex-1 rounded-lg border border-line2 px-2 py-1 text-[15px] font-bold"
            />
            <Button type="submit" size="sm" variant="primary" disabled={umbenennen.isPending}>
              <Check className="size-3.5" aria-hidden /> OK
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setTitelBearbeiten(false)}
            >
              <X className="size-3.5" aria-hidden />
            </Button>
          </form>
        ) : (
          <h2 className="min-w-0 flex-1 truncate text-[17px] font-bold" title={dokument.titel}>
            {dokument.titel}
            <button
              type="button"
              onClick={() => {
                setTitelEntwurf(dokument.titel)
                setTitelBearbeiten(true)
              }}
              title="Umbenennen"
              aria-label="Umbenennen"
              className="ml-1.5 cursor-pointer rounded p-0.5 align-middle text-muted hover:bg-[#f3f2ef] hover:text-ink"
            >
              <Pencil className="size-3.5" aria-hidden />
            </button>
          </h2>
        )}
      </div>

      {/* ── Fassung · wer · wann ────────────────────────────────────── */}
      <div className="text-xs text-ink2">
        {dokument.art === 'ordner' ? (
          <>Ordner · angelegt {zeitpunktLesbar(dokument.createdAt)}</>
        ) : dokument.nummer === null ? (
          <>Noch keine Fassung.</>
        ) : (
          <>
            Fassung {dokument.nummer}
            {dokument.geaendertVon && <> · {dokument.geaendertVon}</>}
            {dokument.geaendertAm && <> · {zeitpunktLesbar(dokument.geaendertAm)}</>}
            {dokument.groesseBytes !== null && <> · {groesseLesbar(dokument.groesseBytes)}</>}
          </>
        )}
      </div>

      {/* ── Handgriffe ──────────────────────────────────────────────── */}
      {!bearbeiten && (
        <div className="flex flex-wrap items-center gap-1.5">
          {dokument.art === 'markdown' && (
            <>
              <Button size="sm" variant="primary" onClick={bearbeitenStarten} disabled={isLoading}>
                <Pencil className="mr-1 inline size-3.5" aria-hidden />
                Bearbeiten
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void kopieren(false)}
                title="Text in die Zwischenablage"
              >
                <Copy className="mr-1 inline size-3.5" aria-hidden />
                Kopieren
              </Button>
              {eltern.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void kopieren(true)}
                  title="Alle übergeordneten Texte und diesen, von oben nach unten, als eine Datei"
                >
                  <Copy className="mr-1 inline size-3.5" aria-hidden />
                  Mit Übergeordneten kopieren
                </Button>
              )}
            </>
          )}
          {dokument.art === 'datei' && (
            <>
              {fassung?.attachmentId && (
                <a
                  href={`/api/anhang/${fassung.attachmentId}`}
                  download={fassung.dateiname ?? undefined}
                  className="tap inline-flex cursor-pointer items-center rounded-lg bg-accent px-[11px] py-[5px] text-xs font-semibold text-white hover:brightness-105"
                >
                  <Download className="mr-1 size-3.5" aria-hidden />
                  Herunterladen
                </a>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => dateiwahl.current?.click()}
                disabled={laedtHoch}
              >
                <Upload className="mr-1 inline size-3.5" aria-hidden />
                {laedtHoch
                  ? 'Lädt hoch …'
                  : dokument.nummer === null
                    ? 'Datei hochladen'
                    : 'Neue Fassung hochladen'}
              </Button>
              <input
                ref={dateiwahl}
                type="file"
                hidden
                aria-label="Datei für die neue Fassung"
                onChange={(e) => {
                  const datei = e.target.files?.[0]
                  e.target.value = ''
                  if (datei) void neueDateiFassung(datei)
                }}
              />
            </>
          )}
          {dokument.art !== 'ordner' && (
            <Button
              size="sm"
              variant={verlaufOffen ? 'soft' : 'ghost'}
              onClick={() => setVerlaufOffen((v) => !v)}
            >
              <History className="mr-1 inline size-3.5" aria-hidden />
              Verlauf
            </Button>
          )}
          <Button
            size="sm"
            variant={verschiebenOffen ? 'soft' : 'ghost'}
            onClick={() => setVerschiebenOffen((v) => !v)}
          >
            Verschieben
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={loeschenFragen}
            disabled={loeschen.isPending}
            className="text-[#b42318]"
          >
            <Trash2 className="mr-1 inline size-3.5" aria-hidden />
            Löschen
          </Button>
          {hinweis && <span className="text-xs text-accent-deep">{hinweis}</span>}
        </div>
      )}

      {fehler && (
        <div
          role="alert"
          className="rounded-lg border border-[#f0c9c4] bg-[#fdf3f2] px-3 py-2 text-xs text-[#b42318]"
        >
          {fehler}
        </div>
      )}

      {/* ── Verschieben ─────────────────────────────────────────────── */}
      {verschiebenOffen && !bearbeiten && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-[#fafaf8] px-3 py-2 text-xs">
          <label htmlFor="verschieben-ziel" className="text-ink2">
            Verschieben nach
          </label>
          <select
            id="verschieben-ziel"
            defaultValue={dokument.elternId ?? ''}
            onChange={(e) => verschieben.mutate(e.target.value || null)}
            className="rounded-lg border border-line2 px-2 py-1"
          >
            <option value="">oben (keine Übergeordnete)</option>
            {moeglicheEltern.map((e) => (
              <option key={e.id} value={e.id}>
                {pfadZu(alle, e.id)
                  .map((p) => p.titel)
                  .join(' › ')}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Verlauf ─────────────────────────────────────────────────── */}
      {verlaufOffen && !bearbeiten && (
        <ol className="flex flex-col gap-px rounded-lg border border-line bg-[#fafaf8] p-1.5 text-xs">
          {versionen.map((v) => {
            const angezeigt = (fassungNummer ?? dokument.nummer) === v.nummer
            return (
              <li
                key={v.nummer}
                className="flex items-center gap-2 rounded px-2 py-1 hover:bg-white"
              >
                <button
                  type="button"
                  onClick={() => setFassungNummer(v.nummer === dokument.nummer ? null : v.nummer)}
                  className={
                    angezeigt
                      ? 'flex-1 cursor-pointer text-left font-semibold'
                      : 'flex-1 cursor-pointer text-left'
                  }
                >
                  Fassung {v.nummer}
                  {v.geaendertVon && <> · {v.geaendertVon}</>}
                  {v.geaendertAm && <> · {zeitpunktLesbar(v.geaendertAm)}</>}
                  {v.dateiname && <> · {v.dateiname}</>}
                  {v.nummer === dokument.nummer && (
                    <span className="ml-1 text-muted">(aktuell)</span>
                  )}
                </button>
                {v.attachmentId && (
                  <a
                    href={`/api/anhang/${v.attachmentId}`}
                    download={v.dateiname ?? undefined}
                    className="text-accent-deep"
                    title="Diese Fassung herunterladen"
                  >
                    <Download className="size-3.5" aria-hidden />
                  </a>
                )}
              </li>
            )
          })}
        </ol>
      )}

      {/* ── Alte Fassung ────────────────────────────────────────────── */}
      {alteFassung && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#f0dcb0] bg-[#fdf8ec] px-3 py-2 text-xs">
          <span>
            Du siehst <b>Fassung {fassungNummer}</b> – die aktuelle ist {dokument.nummer}.
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => wiederherstellen.mutate(fassungNummer!)}
            disabled={wiederherstellen.isPending}
          >
            Wiederherstellen
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setFassungNummer(null)}>
            Zur aktuellen
          </Button>
        </div>
      )}

      {/* ── Inhalt ──────────────────────────────────────────────────── */}
      {bearbeiten ? (
        <div>
          <MarkdownFeld
            wert={entwurf}
            setzen={setEntwurf}
            zeilen={24}
            platzhalter="Markdown. Bilder: ziehen oder Strg+V."
            beiAbsenden={() => speichern.mutate()}
            beiDateien={(dateien) => void bilderHochladen(dateien)}
            laedtHoch={laedtHoch}
            fokusBeimEinhaengen
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Button
              variant="primary"
              size="sm"
              disabled={speichern.isPending || laedtHoch}
              onClick={() => speichern.mutate()}
            >
              {speichern.isPending ? 'Speichert …' : `Speichern als Fassung ${basis + 1}`}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setBearbeiten(false)}>
              Abbrechen
            </Button>
            <span className="text-[11px] text-muted">⌘↵ speichert</span>
          </div>
        </div>
      ) : dokument.art === 'ordner' ? (
        <p className="text-xs text-muted">Ein Ordner – seine Dokumente stehen links darunter.</p>
      ) : isLoading ? (
        <div className="text-xs text-muted">Lädt …</div>
      ) : !fassung ? (
        <p className="text-xs text-muted">
          {dokument.art === 'datei' ? 'Noch keine Datei hochgeladen.' : 'Noch kein Inhalt.'}
        </p>
      ) : dokument.art === 'markdown' ? (
        fassung.inhalt?.trim() ? (
          <article className="text-[13.5px] leading-relaxed">
            <Markdown text={fassung.inhalt} dokument />
          </article>
        ) : (
          <p className="text-xs text-muted">Leer – „Bearbeiten" füllt es.</p>
        )
      ) : fassung.attachmentId ? (
        istBild ? (
          <a
            href={`/api/anhang/${fassung.attachmentId}`}
            target="_blank"
            rel="noreferrer"
            className="block"
          >
            <img
              src={`/api/anhang/${fassung.attachmentId}`}
              alt={fassung.dateiname ?? dokument.titel}
              className="max-h-[70vh] max-w-full rounded-lg border border-line"
            />
          </a>
        ) : (
          <div className="rounded-lg border border-line bg-[#fafaf8] px-3 py-3 text-xs text-ink2">
            {fassung.dateiname} · {fassung.mimeTyp}
            {fassung.groesseBytes !== null && <> · {groesseLesbar(fassung.groesseBytes)}</>}
            <div className="mt-1 text-muted">Wird heruntergeladen, nicht angezeigt.</div>
          </div>
        )
      ) : null}
    </div>
  )
}
